import { Input, OnDestroy, OnInit, Output, EventEmitter, Component } from '@angular/core';
import { NavbarComponent } from '../navbar/navbar.component';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RestService, UserTestListItem, UserTestResumeResponse, RecreateSettings } from '../service/rest/rest.service';
import { AuthService } from '../service/auth/auth.service';
import { LoggerService } from '../service/utils/logger.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-shared-tests',
  standalone: true,
  imports: [NavbarComponent, RouterLink, CommonModule, FormsModule],
  templateUrl: './shared-tests.component.html',
  styleUrl: './shared-tests.component.css'
})
export class SharedTestsComponent implements OnInit, OnDestroy {
  @Input() embedded = false;
  @Output() sharedListLoaded = new EventEmitter<void>();
  sharedTests: UserTestListItem[] = [];
  filteredTests: UserTestListItem[] = [];
  availableSubjects: { id: string; name: string }[] = [];
  selectedMode: 'all' | 'test' | 'exam' = 'all';
  selectedSubjectName: string | null = null;
  isLoading = false;
  sortColumn: string = 'savedDate';
  sortDirection: 'asc' | 'desc' = 'desc';
  private routerSubscription?: Subscription;

  /** Remove-from-list confirm dialog */
  deleteConfirmTestId: number | null = null;
  deleteConfirmLoading = false;

  constructor(
    private restService: RestService,
    private authService: AuthService,
    private router: Router
  ) {
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: unknown) => {
        const e = event as { urlAfterRedirects?: string; url?: string };
        const url: string = e.urlAfterRedirects ?? e.url ?? '';
        if (url.includes('/shared-tests') || url.includes('/study-together')) {
          this.restService.clearRecreateSettings();
          this.restService.clearCurrentTestState();
          this.loadData();
        }
      });
  }

  async ngOnInit() {
    this.restService.clearRecreateSettings();
    this.restService.clearCurrentTestState();
    await this.loadData();
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
  }

  async loadData() {
    this.isLoading = true;
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) {
        LoggerService.error('User not authenticated', null);
        return;
      }
      const tests = await this.restService.getSharedTests(sessionId);
      if (tests) {
        this.sharedTests = tests;
        this.buildAvailableSubjects();
        this.applyFilters();
      } else {
        this.sharedTests = [];
        this.filteredTests = [];
        this.availableSubjects = [];
      }
    } catch (error) {
      LoggerService.error('Failed to load shared tests:', error);
    } finally {
      this.isLoading = false;
      this.sharedListLoaded.emit();
    }
  }

  private buildAvailableSubjects(): void {
    const seen = new Set<string>();
    const subjects: { id: string; name: string }[] = [];
    for (const test of this.sharedTests) {
      const name = test.subjectName || '';
      if (name && !seen.has(name)) {
        seen.add(name);
        subjects.push({ id: name, name });
      }
    }
    subjects.sort((a, b) => a.name.localeCompare(b.name));
    this.availableSubjects = subjects;
  }

  onFilterChange(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    this.filteredTests = this.sharedTests.filter(test => {
      if (this.selectedMode !== 'all' && test.mode !== this.selectedMode) return false;
      if (this.selectedSubjectName != null && test.subjectName !== this.selectedSubjectName) return false;
      return true;
    });
    this.applySorting();
  }

  applySorting() {
    this.filteredTests.sort((a, b) => {
      let comparison = 0;
      switch (this.sortColumn) {
        case 'subjectName':
          comparison = (a.subjectName || '').localeCompare(b.subjectName || '');
          break;
        case 'mode':
          comparison = (a.mode || '').localeCompare(b.mode || '');
          break;
        case 'questionCount':
          comparison = (a.questionCount || 0) - (b.questionCount || 0);
          break;
        case 'completed':
          comparison = (a.completedCount || 0) - (b.completedCount || 0);
          break;
        case 'score':
          comparison = (a.score ?? -1) - (b.score ?? -1);
          break;
        case 'savedDate':
          comparison = (a.savedDate ? new Date(a.savedDate).getTime() : 0) - (b.savedDate ? new Date(b.savedDate).getTime() : 0);
          break;
        default:
          return 0;
      }
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applySorting();
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) return 'bi-arrow-down-up';
    return this.sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
  }

  openDeleteConfirm(testId: number): void {
    this.deleteConfirmTestId = testId;
    this.deleteConfirmLoading = false;
  }

  closeDeleteConfirm(): void {
    if (!this.deleteConfirmLoading) this.deleteConfirmTestId = null;
  }

  /** Remove the share link for the current user (test disappears from this list). */
  async doRemoveShared(): Promise<void> {
    const testId = this.deleteConfirmTestId;
    if (testId == null) return;
    const user = this.authService.getUser();
    const sessionId = user?.sid;
    if (!sessionId) {
      LoggerService.error('User not authenticated', null);
      return;
    }
    this.deleteConfirmLoading = true;
    try {
      const ok = await this.restService.removeSharedWithMe(sessionId, testId);
      if (ok) {
        this.deleteConfirmTestId = null;
        await this.loadData();
      } else {
        alert('Failed to remove shared test.');
      }
    } finally {
      this.deleteConfirmLoading = false;
    }
  }

  /** Navigate to New Test with this test’s params (same as Saved Tests Recreate). */
  async onRecreate(testId: number): Promise<void> {
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) {
        alert('Please log in to recreate a test.');
        return;
      }
      this.restService.clearRecreateSettings();
      this.restService.clearCurrentTestState();
      const resumeData = await this.restService.getTestForResume(sessionId, testId);
      if (!resumeData) {
        alert('Failed to load test data. The test may have been unshared or deleted.');
        return;
      }
      const settings = this.buildRecreateSettings(resumeData);
      this.restService.setRecreateSettings(settings);
      this.router.navigate(['/question-pool'], { state: { recreateSettings: settings } });
    } catch (error) {
      LoggerService.error('Failed to open recreate', error);
      alert('Failed to open New Test with saved settings.');
    }
  }

  private buildRecreateSettings(resumeData: UserTestResumeResponse): RecreateSettings {
    let topicIds: number[] | undefined;
    if (resumeData.topicIds) {
      try {
        const parsed = JSON.parse(resumeData.topicIds);
        if (Array.isArray(parsed)) topicIds = parsed;
      } catch {
        // ignore invalid JSON
      }
    }
    return {
      courseId: resumeData.courseId,
      subjectIds: resumeData.subjectIds ?? [],
      mode: resumeData.mode,
      amountQuestions: resumeData.amountQuestions,
      redFlagMode: resumeData.redFlagMode,
      yellowFlagMode: resumeData.yellowFlagMode,
      greenFlagMode: resumeData.greenFlagMode,
      excludeHidden: resumeData.excludeHidden === 1,
      onlyUnseen: resumeData.onlyUnseen === 1,
      onlyWrong: resumeData.onlyWrong === 1,
      topicIds,
      seedId: resumeData.seedId
    };
  }

  /** Show the original report (same as Saved Tests Report). */
  async onTestReport(testId: number) {
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) {
        LoggerService.error('User not authenticated', null);
        alert('Please log in to view the test report.');
        return;
      }
      const resumeData = await this.restService.getTestForResume(sessionId, testId);
      if (!resumeData) {
        alert('Failed to load test data. The test may have been unshared or deleted.');
        return;
      }
      this.restService.currentUserTestId = resumeData.userTestId;
      this.restService.currentUserTestMode = resumeData.mode;
      this.restService.courseId = resumeData.courseId;
      this.restService.subjectIds = resumeData.subjectIds ?? [];
      const allQuestionIds = resumeData.questions.map(q => q.questionId);
      if (allQuestionIds.length === 0) {
        alert('No questions found for this test.');
        return;
      }
      this.restService.setQuestionIdsForResume(allQuestionIds);
      this.restService.setMaxQuestions(allQuestionIds.length);
      const answerMap = new Map<number, { answer: string, correct: number }>();
      resumeData.questions.forEach(q => {
        if (q.questionAnswer != null) {
          answerMap.set(q.questionId, { answer: q.questionAnswer, correct: q.correct || 0 });
        }
      });
      if (resumeData.subjectIds?.length) {
        await this.restService.loadAndRestoreQuestionsForResume(
          sessionId,
          resumeData.courseId,
          resumeData.subjectIds,
          allQuestionIds,
          answerMap
        );
      }
      this.router.navigate(['/question-pool-results', testId]);
    } catch (error) {
      LoggerService.error('Failed to load test report:', error);
      alert('An error occurred while loading the test report.');
    }
  }

  getOtherSubjectsTooltip(test: UserTestListItem): string {
    if (!test.subjectNames || test.subjectNames.length <= 1) return '';
    return 'Also: ' + test.subjectNames.slice(1).join(', ');
  }

  formatMode(mode: string | null | undefined): string {
    if (mode == null) return '—';
    return mode === 'exam' ? 'Exam' : 'Study';
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch {
      return dateString;
    }
  }

  formatDateShort(dateString: string | null): string {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  }

  formatScore(score: number | null | undefined): string {
    if (score === null || score === undefined) return '—';
    return score.toFixed(1) + '%';
  }

  formatCompleted(completedCount: number | null | undefined, totalCount: number | null | undefined): string {
    if (completedCount == null || totalCount == null) return '—';
    if (completedCount === 0) return '0';
    if (completedCount === totalCount) return completedCount.toString();
    return completedCount.toString();
  }

  isAllCompleted(completedCount: number | null | undefined, totalCount: number | null | undefined): boolean {
    if (completedCount == null || totalCount == null || totalCount === 0) return false;
    return Number(completedCount) === Number(totalCount);
  }

  isAccessible(test: UserTestListItem): boolean {
    return test.accessible !== false;
  }

  accessHint(test: UserTestListItem): string {
    return test.accessBlockedReason || 'Subscribe to access this shared test.';
  }
}
